import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  useQuery,
  ApolloError,
  DocumentNode,
  QueryHookOptions,
  OperationVariables,
  NetworkStatus
} from "@apollo/client";

interface PageInfo {
  endCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
}

interface PaginatedResponse<TNode> {
  pageInfo: PageInfo;
  totalCount: number;
  data: TNode[];
}

interface UsePaginatedQueryParams<TVariables, TNode> {
  query: DocumentNode;
  variables?: TVariables;
  cursorField: string;
  pageSize?: number;
  options?: Omit<
    QueryHookOptions<
      GetPaginatedData<TNode>,
      TVariables extends OperationVariables ? TVariables : never
    >,
    "variables"
  >;
  loadMore?: boolean;
  retainCachedData?: boolean;
  maxCachedPages?: number;
}

interface GetPaginatedData<TNode> {
  [key: string]: PaginatedResponse<TNode>;
}

interface UsePaginatedQueryReturn<TNode> {
  data: TNode[];
  pageInfo: PageInfo;
  totalCount: number;
  loading: boolean;
  isLoadingMore: boolean;
  isRefetching: boolean;
  error: ApolloError | undefined;
  loadNextPage: () => void;
  loadPreviousPage: () => void;
  goToPage: (pageNumber: number) => void;
  setPageSize: (newPageSize: number) => void;
  resetPagination: () => void;
  refetchCurrentPage: () => Promise<any>;
  refresh: () => Promise<void>;
  currentPage: number;
}

export default function usePaginatedQuery<TVariables extends OperationVariables, TNode>({
  query,
  variables = {} as TVariables,
  cursorField,
  pageSize: initialPageSize = 10,
  options,
  loadMore = false,
  retainCachedData = true,
  maxCachedPages = 10
}: UsePaginatedQueryParams<TVariables, TNode>): UsePaginatedQueryReturn<TNode> {
  const [pagination, setPagination] = useState<{
    after: string | null;
    before: string | null;
    first: number | null;
    last: number | null;
    currentPage: number;
    pageSize: number;
    offset: number | null;
  }>({
    after: null,
    before: null,
    first: initialPageSize,
    last: null,
    currentPage: 1,
    pageSize: initialPageSize,
    offset: null
  });

  // Add state to track page jumping
  const [targetPage, setTargetPage] = useState<number | null>(null);

  const [pagesData, setPagesData] = useState<Map<number, TNode[]>>(new Map());
  const [totalCount, setTotalCount] = useState(0);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    endCursor: null,
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: null
  });

  // Track previous variables to detect changes
  const previousVariablesRef = useRef<string>("");
  const currentVariablesStr = JSON.stringify(variables);

  const { data, loading, error, networkStatus, refetch } = useQuery<
    GetPaginatedData<TNode>,
    TVariables
  >(query, {
    variables: {
      ...variables,
      first: pagination.first,
      after: pagination.after,
      before: pagination.before,
      last: pagination.last,
      offset: pagination.offset
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    ...options
  });

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  const isRefetching = networkStatus === NetworkStatus.refetch;

  const paginatedData = data?.[cursorField] as PaginatedResponse<TNode> | undefined;

  // Reset pagination when variables change
  useEffect(() => {
    if (previousVariablesRef.current && previousVariablesRef.current !== currentVariablesStr) {
      resetPagination();
    }
    previousVariablesRef.current = currentVariablesStr;
  }, [currentVariablesStr]);

  // Handle page navigation when there's a target page
  useEffect(() => {
    if (targetPage !== null && !loading && paginatedData?.data) {
      // If we've reached our target page, reset target
      if (pagination.currentPage === targetPage) {
        setTargetPage(null);
        return;
      }

      // If we have a next page and haven't reached our target, keep going
      if (paginatedData.pageInfo.hasNextPage && pagination.currentPage < targetPage) {
        setPagination((prev) => ({
          ...prev,
          after: paginatedData.pageInfo.endCursor,
          before: null,
          first: prev.pageSize,
          last: null,
          currentPage: prev.currentPage + 1
        }));
      } else {
        // If we can't go further but haven't reached target, reset target
        setTargetPage(null);
      }
    }
  }, [loading, paginatedData, pagination.currentPage, targetPage]);

  useEffect(() => {
    if (!paginatedData?.data) return;

    if (!loadMore || !retainCachedData) {
      // Replace current page data or all data
      setPagesData(new Map([[pagination.currentPage, paginatedData.data]]));
    } else {
      // Update or add current page data
      setPagesData((prev) => {
        const newPages = new Map(prev);
        newPages.set(pagination.currentPage, paginatedData.data);

        // Limit the number of cached pages to prevent memory issues
        if (maxCachedPages > 0 && newPages.size > maxCachedPages) {
          // Keep most recent pages
          const sortedKeys = Array.from(newPages.keys()).sort((a, b) => b - a);
          while (newPages.size > maxCachedPages) {
            const oldestKey = sortedKeys.pop();
            if (oldestKey !== undefined) newPages.delete(oldestKey);
          }
        }

        return newPages;
      });
    }

    setTotalCount(paginatedData.totalCount);
    setPageInfo(paginatedData.pageInfo);
  }, [
    paginatedData?.data,
    paginatedData?.totalCount,
    paginatedData?.pageInfo?.endCursor,
    paginatedData?.pageInfo?.startCursor,
    pagination.currentPage,
    loadMore,
    retainCachedData,
    maxCachedPages
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setPagesData(new Map());
      setTotalCount(0);
      setPagination({
        after: null,
        before: null,
        first: initialPageSize,
        last: null,
        currentPage: 1,
        pageSize: initialPageSize,
        offset: null
      });
    };
  }, [initialPageSize]);

  const loadNextPage = useCallback(() => {
    if (pageInfo.hasNextPage) {
      setPagination((prev) => ({
        ...prev,
        after: pageInfo.endCursor,
        before: null,
        first: prev.pageSize,
        last: null,
        currentPage: prev.currentPage + 1
      }));
    }
  }, [pageInfo]);

  const loadPreviousPage = useCallback(() => {
    if (pageInfo.hasPreviousPage) {
      setPagination((prev) => ({
        ...prev,
        after: null,
        before: pageInfo.startCursor,
        first: null,
        last: prev.pageSize,
        currentPage: Math.max(prev.currentPage - 1, 1)
      }));
    }
  }, [pageInfo]);

  const goToPage = useCallback(
    (pageNumber: number) => {
      resetPagination();
      // For other pages, we'll convert the offset to a cursor using Relay's arrayconnection format
      const offset = (pageNumber - 1) * pagination.pageSize;
      const offsetToCursor = (offset: number) => btoa(`arrayconnection:${offset}`);
      const cursor = offsetToCursor(offset);

      // Reset data and use the generated cursor
      setPagesData(new Map());
      setPagination({
        ...pagination,
        after: cursor,
        before: null,
        first: pagination.pageSize,
        last: null,
        currentPage: pageNumber,
        offset: null // Not needed when using cursor
      });
      setTargetPage(pageNumber);
    },
    [pagination.pageSize, pageInfo, pagination.currentPage]
  );

  const setPageSizeFn = useCallback((newPageSize: number) => {
    setPagesData(new Map());
    setPagination({
      after: null,
      before: null,
      first: newPageSize,
      last: null,
      currentPage: 1,
      pageSize: newPageSize,
      offset: null
    });
    setTotalCount(0);
    setTargetPage(null);
  }, []);

  const resetPagination = useCallback(() => {
    setPagesData(new Map());
    setPagination({
      after: null,
      before: null,
      first: initialPageSize,
      last: null,
      currentPage: 1,
      pageSize: initialPageSize,
      offset: null
    });
    setTotalCount(0);
    setTargetPage(null);
  }, [initialPageSize]);

  const refetchCurrentPage = useCallback(() => {
    return refetch();
  }, [refetch]);

  const pendingRefreshRef = useRef<{ resolve: () => void; sawLoading: boolean } | null>(null);

  useEffect(() => {
    const pending = pendingRefreshRef.current;
    if (!pending) return;
    if (loading) pending.sawLoading = true;
    else if (pending.sawLoading) {
      pendingRefreshRef.current = null;
      pending.resolve();
    }
  }, [loading, pagination.currentPage]);

  const refresh = useCallback((): Promise<void> => {
    if (pagination.currentPage === 1)
      return refetch().then(
        () => undefined,
        () => undefined
      );
    pendingRefreshRef.current?.resolve();
    resetPagination();
    return new Promise<void>((resolve) => {
      pendingRefreshRef.current = { resolve, sawLoading: false };
    });
  }, [pagination.currentPage, refetch, resetPagination]);

  const mergedData = useMemo(
    () => (pagesData.size > 0 ? Array.from(pagesData.values()).flat() : paginatedData?.data ?? []),
    [pagesData, paginatedData?.data]
  );

  return {
    data: mergedData,
    pageInfo,
    totalCount,
    loading,
    isLoadingMore,
    isRefetching,
    error,
    loadNextPage,
    loadPreviousPage,
    goToPage,
    setPageSize: setPageSizeFn,
    resetPagination,
    refetchCurrentPage,
    refresh,
    currentPage: pagination.currentPage
  };
}
